import React, { useEffect, useState } from "react";
import {
  Euro, Plus, TrendingUp, AlertCircle, CheckCircle2, Loader2, Trash2, Users, ChevronRight, X, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

const STATUS_META = {
  trial: { label: "Essai", cls: "bg-blue-500/10 text-blue-300" },
  active: { label: "Actif", cls: "bg-emerald-500/10 text-emerald-300" },
  overdue: { label: "En retard", cls: "bg-red-500/10 text-red-300" },
  paused: { label: "Pause", cls: "bg-zinc-500/10 text-zinc-300" },
  cancelled: { label: "Résilié", cls: "bg-zinc-500/10 text-zinc-500" },
};
const CADENCE_LABEL = { monthly: "/mois", annually: "/an", one_off: "one-shot" };

export default function Subscriptions() {
  const [subs, setSubs] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState("all");
  const [appFilter, setAppFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, k, a] = await Promise.all([
        api.get("/subscriptions"),
        api.get("/subscriptions/kpis"),
        api.get("/settings/saas-apps"),
      ]);
      setSubs(s.data.subscriptions || []);
      setKpis(k.data);
      setApps(a.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const markPaid = async (id) => {
    try {
      await api.post(`/subscriptions/${id}/mark-paid`);
      toast.success("Paiement enregistré, prochain terme calculé");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const del = async (s) => {
    if (!window.confirm(`Supprimer l'abonnement de ${s.user_email} ?`)) return;
    try { await api.delete(`/subscriptions/${s.id}`); toast.success("Supprimé"); refresh(); } catch { toast.error("Erreur"); }
  };

  const filtered = subs.filter((s) => {
    if (filter !== "all" && s.status !== filter) return false;
    if (appFilter !== "all" && (s.app_id || "chantix") !== appFilter) return false;
    return true;
  });

  const appById = Object.fromEntries(apps.map((a) => [a.code, a]));

  return (
    <ConsoleShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="subscriptions-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">ULRICH · Finance</div>
            <h1 className="text-3xl sm:text-4xl font-light text-white tracking-tight">Abonnements & paiements</h1>
            <p className="text-sm text-zinc-400 mt-2">MRR live · dunning auto · aucun paiement raté.</p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-sub-btn"
            className="inline-flex items-center gap-2 bg-emerald-500 text-black px-4 py-2 text-sm font-medium hover:bg-emerald-400 transition"
          >
            <Plus className="w-4 h-4" /> Nouvel abonnement
          </button>
        </div>

        {/* KPIs */}
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="MRR" value={`${kpis.mrr.toFixed(0)} €`} icon={TrendingUp} color="emerald" sub={`ARR ${kpis.arr.toFixed(0)} €`} testid="kpi-mrr" />
            <KPI label="Actifs" value={kpis.counts.active} icon={CheckCircle2} color="emerald" sub={`${kpis.counts.trial} en essai`} testid="kpi-active" />
            <KPI label="En retard" value={kpis.counts.overdue} icon={AlertCircle} color="red" sub={`${kpis.overdue_amount.toFixed(0)} € à recouvrer`} testid="kpi-overdue" />
            <KPI label="À risque" value={kpis.at_risk} icon={Sparkles} color="amber" sub="essais < 7j" testid="kpi-atrisk" />
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            {["all", "trial", "active", "overdue", "cancelled"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-[11px] px-3 py-1.5 border transition ${filter === f ? "border-white/30 text-white bg-white/5" : "border-white/10 text-zinc-400 hover:text-white"}`}
                data-testid={`filter-${f}`}
              >
                {f === "all" ? "Tous" : STATUS_META[f]?.label}
              </button>
            ))}
          </div>
          {apps.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap border-l border-white/10 pl-3">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">App</span>
              <button
                onClick={() => setAppFilter("all")}
                className={`text-[11px] px-3 py-1.5 border transition ${appFilter === "all" ? "border-white/30 text-white bg-white/5" : "border-white/10 text-zinc-400 hover:text-white"}`}
                data-testid="app-filter-all"
              >Toutes</button>
              {apps.map((a) => (
                <button
                  key={a.code}
                  onClick={() => setAppFilter(a.code)}
                  className={`text-[11px] px-3 py-1.5 border transition inline-flex items-center gap-1.5 ${appFilter === a.code ? "border-white/30 text-white bg-white/5" : "border-white/10 text-zinc-400 hover:text-white"}`}
                  data-testid={`app-filter-${a.code}`}
                >
                  {a.name}
                  {a.coverage_mode === "finance_only" && <span className="text-[8px] text-amber-400/70">$</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-zinc-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-white/10 p-10 text-center space-y-2">
            <Users className="w-8 h-8 text-zinc-600 mx-auto" />
            <div className="text-sm text-zinc-300">Aucun abonnement {filter === "all" ? "" : `avec statut ${STATUS_META[filter]?.label}`}</div>
            <div className="text-xs text-zinc-500">Ajoute un abonné pour démarrer le suivi</div>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((s) => <SubRow key={s.id} s={s} app={appById[s.app_id || "chantix"]} onMarkPaid={() => markPaid(s.id)} onDelete={() => del(s)} />)}
          </div>
        )}
      </div>

      {showAdd && <AddModal apps={apps} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); refresh(); }} />}
    </ConsoleShell>
  );
}

function KPI({ label, value, icon: Icon, color, sub, testid }) {
  const colors = {
    emerald: "text-emerald-400 border-emerald-500/20 bg-emerald-500/[0.03]",
    red: "text-red-400 border-red-500/20 bg-red-500/[0.03]",
    amber: "text-amber-400 border-amber-500/20 bg-amber-500/[0.03]",
  };
  return (
    <div className={`border p-4 ${colors[color]}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="text-2xl font-light text-white">{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function SubRow({ s, app, onMarkPaid, onDelete }) {
  const st = STATUS_META[s.status] || STATUS_META.trial;
  const cadence = CADENCE_LABEL[s.cadence] || "";
  const due = s.next_due_at ? new Date(s.next_due_at).toLocaleDateString("fr-FR") : "—";
  return (
    <div className="border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] p-4 flex items-start gap-4" data-testid={`sub-row-${s.id}`}>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{s.user_name || s.user_email}</span>
          <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-wider ${st.cls}`}>{st.label}</span>
          {app && (
            <span className="text-[9px] px-1.5 py-0.5 uppercase tracking-wider border border-blue-500/30 text-blue-300 bg-blue-500/5">
              {app.name}
            </span>
          )}
          {s.days_overdue > 0 && <span className="text-[10px] text-red-400">+{s.days_overdue}j retard</span>}
        </div>
        <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
          <span className="font-mono">{s.user_email}</span>
          <span>·</span>
          <span>{s.plan_name}</span>
          <span>·</span>
          <span className="text-zinc-300 font-mono">{s.amount_ht}€{cadence}</span>
          <span>·</span>
          <span>Échéance {due}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {(s.status === "overdue" || s.status === "active" || s.status === "trial") && (
          <button
            onClick={onMarkPaid}
            className="text-[11px] inline-flex items-center gap-1 border border-emerald-500/40 text-emerald-300 px-2.5 py-1.5 hover:bg-emerald-500/10"
            data-testid={`mark-paid-${s.id}`}
          >
            <Euro className="w-3 h-3" /> Payé
          </button>
        )}
        <button onClick={onDelete} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function AddModal({ apps, onClose, onCreated }) {
  const defaultApp = apps[0]?.code || "chantix";
  const [f, setF] = useState({ user_email: "", user_name: "", app_id: defaultApp, plan_name: "Standard", amount_ht: 29, cadence: "monthly", status: "trial", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.user_email.trim()) { toast.error("Email requis"); return; }
    setSaving(true);
    try {
      await api.post("/subscriptions", f);
      toast.success("Abonnement ajouté");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-black border border-white/10 w-full max-w-lg p-6 space-y-4" data-testid="add-sub-modal">
        <div className="flex items-center justify-between">
          <div className="text-lg font-medium">Nouvel abonnement</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <Fld label="Email abonné *" v={f.user_email} on={(v) => set("user_email", v)} tid="new-email" />
        <Fld label="Nom" v={f.user_name} on={(v) => set("user_name", v)} tid="new-name" />
        {apps.length > 1 && (
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1">App concernée</label>
            <select
              value={f.app_id}
              onChange={(e) => set("app_id", e.target.value)}
              data-testid="new-app"
              className="w-full bg-black/40 border border-white/10 text-sm px-3 py-2"
            >
              {apps.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.name}{a.coverage_mode === "finance_only" ? " · finance seule" : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Plan" v={f.plan_name} on={(v) => set("plan_name", v)} tid="new-plan" />
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1">Montant HT (€)</label>
            <input type="number" value={f.amount_ht} onChange={(e) => set("amount_ht", parseFloat(e.target.value) || 0)} data-testid="new-amount" className="w-full bg-black/40 border border-white/10 text-sm px-3 py-2" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1">Cadence</label>
            <select value={f.cadence} onChange={(e) => set("cadence", e.target.value)} data-testid="new-cadence" className="w-full bg-black/40 border border-white/10 text-sm px-3 py-2">
              <option value="monthly">Mensuel</option>
              <option value="annually">Annuel</option>
              <option value="one_off">One-shot</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1">Statut</label>
            <select value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="new-status" className="w-full bg-black/40 border border-white/10 text-sm px-3 py-2">
              <option value="trial">Essai</option>
              <option value="active">Actif</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-xs px-4 py-2 border border-white/15 hover:bg-white/5">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="new-save" className="text-xs px-4 py-2 bg-emerald-500 text-black inline-flex items-center gap-2 hover:bg-emerald-400 disabled:opacity-40">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Créer
          </button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, v, on, tid }) {
  return (
    <div>
      <label className="text-[10px] uppercase text-zinc-500 block mb-1">{label}</label>
      <input type="text" value={v} onChange={(e) => on(e.target.value)} data-testid={tid} className="w-full bg-black/40 border border-white/10 text-sm px-3 py-2" />
    </div>
  );
}
